import React, { useMemo } from 'react';

const Positions = React.memo(({ position = null, symbol = 'AMBER' }) => {
  const formatNumber = (num) => {
    if (num === null || num === undefined) return '—';
    return typeof num === 'number' ? num.toFixed(2) : '—';
  };

  const formatCurrency = (num) => {
    if (num === null || num === undefined) return '—';
    return typeof num === 'number' ? num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';
  };

  const getPositionColor = (netPosition) => {
    if (netPosition > 0) return 'var(--good)'; // Green for long
    if (netPosition < 0) return 'var(--bad)'; // Red for short
    return 'var(--text-secondary)'; // Gray for zero
  };

  const getPositionLabel = (netPosition) => {
    if (netPosition > 0) return 'LONG';
    if (netPosition < 0) return 'SHORT';
    return 'FLAT';
  };

  if (!position) {
    return (
      <div className="positions-container">
        <h3>Positions</h3>
        <div className="positions-loading">Loading position data...</div>
      </div>
    );
  }

  const netPosition = position.net_position || 0;
  const buyQuantity = position.buy_quantity || 0;
  const sellQuantity = position.sell_quantity || 0;
  const avgBuyPrice = position.avg_buy_price || 0;
  const avgSellPrice = position.avg_sell_price || 0;
  const totalCost = position.total_cost || 0;
  const totalProceeds = position.total_proceeds || 0;
  const unrealizedPnl = position.unrealized_pnl || 0;

  // Calculate realized P&L (proceeds from sells - cost of buys that were sold)
  // This is a simplified calculation - actual realized P&L would need trade-by-trade matching
  const realizedPnl = totalProceeds - (avgBuyPrice * sellQuantity);

  return (
    <div className="positions-container">
      <h3>Positions - {position.symbol || symbol}</h3>
      <div className="positions-content">
        <div className="position-summary">
          <div className="position-main">
            <div className="position-label" style={{ color: getPositionColor(netPosition) }}>
              {getPositionLabel(netPosition)}
            </div>
            <div className="position-value" style={{ color: getPositionColor(netPosition) }}>
              {formatNumber(Math.abs(netPosition))}
            </div>
          </div>
        </div>

        <table className="positions-table">
          <tbody>
            <tr>
              <td className="positions-label">Net Position:</td>
              <td className="positions-value" style={{ color: getPositionColor(netPosition) }}>
                {formatNumber(netPosition)}
              </td>
            </tr>
            <tr>
              <td className="positions-label">Buy Quantity:</td>
              <td className="positions-value">{formatNumber(buyQuantity)}</td>
            </tr>
            <tr>
              <td className="positions-label">Sell Quantity:</td>
              <td className="positions-value">{formatNumber(sellQuantity)}</td>
            </tr>
            <tr>
              <td className="positions-label">Avg Buy Price:</td>
              <td className="positions-value">{formatNumber(avgBuyPrice)}</td>
            </tr>
            <tr>
              <td className="positions-label">Avg Sell Price:</td>
              <td className="positions-value">{formatNumber(avgSellPrice)}</td>
            </tr>
            <tr>
              <td className="positions-label">Total Cost:</td>
              <td className="positions-value">{formatCurrency(totalCost)}</td>
            </tr>
            <tr>
              <td className="positions-label">Total Proceeds:</td>
              <td className="positions-value">{formatCurrency(totalProceeds)}</td>
            </tr>
            {realizedPnl !== 0 && (
              <tr>
                <td className="positions-label">Realized P&L:</td>
                <td className="positions-value" style={{ color: realizedPnl >= 0 ? 'var(--good)' : 'var(--bad)' }}>
                  {formatCurrency(realizedPnl)}
                </td>
              </tr>
            )}
            {unrealizedPnl !== 0 && (
              <tr>
                <td className="positions-label">Unrealized P&L:</td>
                <td className="positions-value" style={{ color: unrealizedPnl >= 0 ? 'var(--good)' : 'var(--bad)' }}>
                  {formatCurrency(unrealizedPnl)}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {position.timestamp && (
          <div className="positions-timestamp">
            Last updated: {new Date(position.timestamp).toLocaleString()}
          </div>
        )}
      </div>
    </div>
  );
});

Positions.displayName = 'Positions';

export default Positions;


